// src/views/parent/PAEntreesSorties.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";

// ─── Helper : date du jour en heure LOCALE (évite le décalage UTC) ───
function getTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function PAEntreesSorties({ slug }) {
  const { enfantId } = useParams();
  const navigate = useNavigate();
  const { getPresencesEnfant } = useParentService();

  const [presences, setPresences] = useState([]);
  const [filteredPresences, setFilteredPresences] = useState([]);
  // ⬇️ Initialise avec la date du jour (cohérent avec ce que l'input affiche)
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMotif, setSelectedMotif] = useState({ show: false, motif: "" });

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES DONNÉES ─────────────────────────────────────────
  const loadPresences = useCallback(async () => {
    if (!slug || !enfantId) {
      setError("Paramètres manquants (parent ou élève).");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getPresencesEnfant(slug, enfantId);
      setPresences(data || []);
      setFilteredPresences(data || []);
    } catch (err) {
      setError("Impossible de récupérer les présences.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enfantId]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadPresences();
  }, [loadPresences]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [enfantId]);

  // ─── FILTRAGE PAR DATE ──────────────────────────────────────────────
  useEffect(() => {
    if (selectedDate) {
      setFilteredPresences(presences.filter((p) => p.date === selectedDate));
    } else {
      setFilteredPresences(presences);
    }
  }, [selectedDate, presences]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const openMotifModal = (motif) => {
    setSelectedMotif({
      show: true,
      motif: motif || "Aucun motif spécifié",
    });
  };

  const closeMotifModal = () => {
    setSelectedMotif({ show: false, motif: "" });
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .es-page {
          min-height: 100vh;
          background: var(--s2);
          padding-bottom: 24px;
        }

        /* ─── Header : bouton sur sa ligne, titre centré dessous ─── */
        .es-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 20px 16px 32px;
          color: #fff;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          overflow: hidden;
        }

        .es-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        /* Ligne du haut : bouton retour à gauche */
        .es-header-top {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .es-back-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: #fff;
          border-radius: 12px;
          cursor: pointer;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          transition: background 0.2s;
          flex-shrink: 0;
        }

        .es-back-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        /* Zone titre + sous-titre centrée */
        .es-header-title {
          text-align: center;
        }

        .es-header-title h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 4px;
          line-height: 1.3;
        }

        .es-header-title p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          opacity: 0.9;
          margin: 0;
          line-height: 1.4;
        }

        /* ─── Loader intégré ─── */
        .es-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .es-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: es-spin 0.8s linear infinite;
        }

        @keyframes es-spin { to { transform: rotate(360deg); } }

        .es-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* ─── Carte d'erreur intégrée ─── */
        .es-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .es-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .es-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .es-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .es-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .es-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .es-error .btn:hover { opacity: 0.9; }

        .es-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        /* ─── Filtres ─── */
        .es-filters {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--s);
          padding: 12px 16px;
          border-radius: 12px;
          margin: 16px 16px 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          flex-wrap: wrap;
        }

        .es-filters .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 200px;
        }

        .es-filters input[type="date"] {
          border: 1px solid var(--b2);
          border-radius: 8px;
          padding: 8px 12px;
          flex: 1;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          background: var(--s);
          color: var(--t);
        }

        .es-filters input[type="date"]:focus {
          border-color: var(--pr);
          box-shadow: 0 0 0 0.25rem var(--pr-bg);
        }

        .es-filters .clear-date {
          border: none;
          background: transparent;
          color: var(--rd);
          cursor: pointer;
          padding: 4px;
          font-size: 18px;
        }

        .es-stats-btn {
          background: var(--pr);
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 10px 18px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
          white-space: nowrap;
        }

        .es-stats-btn:hover {
          background: var(--pr-lt);
        }

        /* ─── Liste ─── */
        .es-list {
          padding: 16px;
          max-height: 60vh;
          overflow-y: auto;
        }

        .es-empty {
          text-align: center;
          padding: 40px 20px;
          color: var(--m);
          background: var(--s);
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .es-empty i {
          font-size: 48px;
          opacity: 0.5;
          margin-bottom: 12px;
          display: block;
        }

        .es-empty p {
          margin: 0;
          font-size: 15px;
        }

        .es-card {
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          background: var(--s);
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          transition: all 0.3s ease;
          border: 1px solid var(--b);
          margin-bottom: 12px;
        }

        .es-card:hover {
          box-shadow: 0 6px 16px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }

        .es-card .status-bar {
          width: 6px;
          flex-shrink: 0;
        }

        .es-card .status-bar.entree { background: var(--gn); }
        .es-card .status-bar.sortie { background: var(--rd); }

        .es-card .card-body {
          padding: 16px 20px;
          flex: 1;
        }

        .es-card .card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }

        .es-card .card-row + .card-row { margin-top: 8px; }

        .es-card .date-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 14px;
          color: var(--t);
        }

        .es-card .date-label i { color: var(--pr); }

        .es-card .type-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .es-card .type-badge.entree {
          background: var(--gn-bg);
          color: var(--gn);
        }

        .es-card .type-badge.sortie {
          background: var(--rd-bg);
          color: var(--rd-dk);
        }

        .es-card .info-text {
          color: var(--m);
          font-size: 13px;
        }

        .es-card .info-text strong { color: var(--t); }

        .es-card .statut {
          font-weight: 700;
          text-transform: uppercase;
          font-size: 13px;
        }

        .es-card .statut.present { color: var(--gn); }
        .es-card .statut.retard { color: var(--am); }
        .es-card .statut.absent { color: var(--rd); }

        .es-card .justifie-badge {
          background: var(--gn-bg);
          color: var(--gn);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .es-card .motif-btn {
          background: transparent;
          border: 1px solid var(--pr);
          color: var(--pr);
          padding: 4px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .es-card .motif-btn:hover {
          background: var(--pr);
          color: #fff;
        }

        .es-card .no-motif {
          color: var(--h);
          font-size: 13px;
        }

        .es-count {
          text-align: center;
          color: var(--m);
          font-size: 13px;
          padding: 0 16px 8px;
        }

        /* ─── Modal ─── */
        .es-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }

        .es-modal {
          background: var(--s);
          border-radius: 16px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }

        .es-modal .close-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--m);
          font-size: 20px;
          padding: 4px;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .es-modal .close-btn:hover { background: var(--s2); }

        .es-modal h5 {
          font-family: 'Poppins', sans-serif;
          color: var(--pr);
          margin: 0 0 12px;
          font-size: 18px;
          font-weight: 700;
        }

        .es-modal .motif-content {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          text-align: justify;
          white-space: pre-wrap;
          word-wrap: break-word;
          line-height: 1.6;
          background: var(--s2);
          padding: 16px;
          border-radius: 8px;
          border: 1px solid var(--b);
        }

        /* ─── Scrollbar ─── */
        .es-list::-webkit-scrollbar { width: 4px; }
        .es-list::-webkit-scrollbar-track { background: var(--s2); }
        .es-list::-webkit-scrollbar-thumb {
          background: var(--b2);
          border-radius: 4px;
        }
        .es-list::-webkit-scrollbar-thumb:hover { background: var(--m); }

        @media (max-width: 480px) {
          .es-header { padding: 16px 12px 28px; }
          .es-header-title h2 { font-size: 17px; }
          .es-header-title p { font-size: 12px; }
          .es-filters {
            padding: 10px 12px;
            margin: 12px 12px 0;
          }
          .es-filters .filter-group { min-width: 100%; }
          .es-stats-btn {
            width: 100%;
            justify-content: center;
          }
          .es-card .card-body { padding: 12px 14px; }
          .es-card .card-row {
            flex-direction: column;
            align-items: flex-start;
          }
          .es-modal { padding: 16px; }
        }
      `}</style>

      <div className="es-page">
        {/* Header : bouton sur sa ligne, titre/sous-titre centrés dessous */}
        <div className="es-header">
          <div className="es-header-top">
            <button
              className="es-back-btn"
              onClick={() => navigate(-1)}
              aria-label="Retour"
            >
              <i className="ti ti-arrow-left" />
            </button>
          </div>

          <div className="es-header-title">
            <h2>
              <i className="ti ti-calendar-check" style={{ marginRight: 8 }} />
              Historique des Présences
            </h2>
            <p>Consultez ici les pointages d'entrée et de sortie de votre enfant</p>
          </div>
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="es-loading">
            <div className="spinner"></div>
            <span>Chargement des présences…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="es-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button
                className="btn"
                onClick={() => {
                  hasFetchedRef.current = false;
                  loadPresences();
                }}
              >
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            {/* Filtres */}
            <div className="es-filters">
              <div className="filter-group">
                <i className="ti ti-filter" style={{ color: "var(--pr)" }} />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
                {selectedDate && (
                  <button className="clear-date" onClick={() => setSelectedDate("")}>
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>

              <button
                className="es-stats-btn"
                onClick={() => navigate(`/${slug}/parent/statistiques/${enfantId}`)}
              >
                <i className="ti ti-chart-bar" />
                Voir Statistiques
              </button>
            </div>

            {/* Liste des présences */}
            <div className="es-list">
              {filteredPresences.length === 0 ? (
                <div className="es-empty">
                  <i className="ti ti-calendar" />
                  <p>
                    Aucune présence trouvée{" "}
                    {selectedDate ? `pour le ${formatDate(selectedDate)}` : ""}
                  </p>
                </div>
              ) : (
                <>
                  {filteredPresences.map((p) => {
                    const isEntree = p.type_pointage === "entree";
                    const statutClass =
                      p.statut === "RETARD" ? "retard"
                      : p.statut === "PRESENT" ? "present"
                      : p.statut === "ABSENT" ? "absent"
                      : "";

                    return (
                      <div key={p.id} className="es-card">
                        <div className={`status-bar ${isEntree ? "entree" : "sortie"}`} />

                        <div className="card-body">
                          <div className="card-row">
                            <span className="date-label">
                              <i className="ti ti-clock" />
                              {formatDate(p.date)}
                            </span>
                            <span className={`type-badge ${isEntree ? "entree" : "sortie"}`}>
                              <i className={`ti ${isEntree ? "ti-login" : "ti-logout"}`} />
                              {isEntree ? "Entrée" : "Sortie"}
                            </span>
                          </div>

                          <div className="card-row">
                            <span className="info-text">
                              🕒 <strong>Heure:</strong> {p.heure || "-"}
                            </span>
                            <span className="info-text">
                              <i className="ti ti-info-circle" style={{ marginRight: 4 }} />
                              <strong>Période:</strong>{" "}
                              {p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace("_", " ") || "-"}
                            </span>
                          </div>

                          <div className="card-row">
                            <span className="info-text">
                              <strong>Statut:</strong>{" "}
                              <span className={`statut ${statutClass}`}>
                                {p.statut || "-"}
                              </span>
                              {p.est_justifie && (
                                <span className="justifie-badge" style={{ marginLeft: 8 }}>
                                  <i className="ti ti-check" /> Justifié
                                </span>
                              )}
                            </span>

                            {p.motif_sortie ? (
                              <button
                                className="motif-btn"
                                onClick={() => openMotifModal(p.motif_sortie)}
                              >
                                <i className="ti ti-eye" />
                                Détail motif
                              </button>
                            ) : (
                              <span className="no-motif">Aucun motif</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Indicateur du nombre de résultats */}
            {filteredPresences.length > 0 && (
              <div className="es-count">
                {filteredPresences.length} présence(s) trouvée(s)
                {selectedDate && ` pour le ${formatDate(selectedDate)}`}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Motif */}
      {selectedMotif.show && (
        <div className="es-modal-overlay" onClick={closeMotifModal}>
          <div className="es-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeMotifModal}>
              <i className="ti ti-x" />
            </button>
            <h5>
              <i className="ti ti-file-text" style={{ marginRight: 8 }} />
              Motif de sortie
            </h5>
            <div className="motif-content">{selectedMotif.motif}</div>
          </div>
        </div>
      )}
    </>
  );
}